import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Box, ButtonBase, Dialog, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { callApi } from '../../../utils/apiCall';
import { chatPhotoPath, photoCaption } from './chatPhotos';
import { isSafeImageUrl } from '../../../components/attachmentsUtils';

// -------------------------------------------------------------------------------------------

/**
 * Loads a fresh signed URL for a chat photo. Signed URLs expire, so `refetch()` (called once from
 * <img onError>) asks for a new one; bustCache stops callApi's in-flight dedupe handing back a stale URL.
 */
export function useChatPhotoUrl(practiceId, leadId, photoId) {
  const [state, setState] = useState({ status: 'loading', url: '' });
  const retried = useRef(false);

  const load = useCallback(async () => {
    const path = chatPhotoPath(practiceId, leadId, photoId);
    if (!path) {
      setState({ status: 'error', url: '' });
      return;
    }
    setState({ status: 'loading', url: '' });
    try {
      const res = await callApi(path, 'GET', null, false, { bustCache: true });
      const url = res?.status === 200 && isSafeImageUrl(res?.data?.url) ? res.data.url : '';
      setState(url ? { status: 'ready', url } : { status: 'error', url: '' });
    } catch {
      setState({ status: 'error', url: '' });
    }
  }, [practiceId, leadId, photoId]);

  useEffect(() => {
    retried.current = false;
    load();
  }, [load]);

  const refetch = useCallback(() => {
    if (retried.current) {
      setState({ status: 'error', url: '' });
      return;
    }
    retried.current = true;
    load();
  }, [load]);

  return { ...state, refetch };
}

function ChatPhoto({ practiceId, leadId, photoId, alt, size, contain }) {
  const { status, url, refetch } = useChatPhotoUrl(practiceId, leadId, photoId);
  if (status === 'loading') return <Skeleton data-testid="chat-photo-skeleton" variant="rounded" width={size} height={size} />;
  if (status === 'error') {
    return (
      <Box
        sx={{ width: size, height: size, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}
      >
        <Typography variant="caption" color="text.secondary" align="center">
          Photo unavailable
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      onError={refetch}
      sx={{ width: size, height: size, borderRadius: 1.5, objectFit: contain ? 'contain' : 'cover', display: 'block' }}
    />
  );
}

ChatPhoto.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  photoId: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contain: PropTypes.bool,
};
ChatPhoto.defaultProps = { practiceId: undefined, leadId: undefined, photoId: undefined, alt: '', size: 96, contain: false };

/** Thumbnail + angle caption. Click calls onOpen. */
export function ChatPhotoThumb({ practiceId, leadId, photo, onOpen }) {
  const label = photoCaption(photo);
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <ButtonBase onClick={onOpen} aria-label={`Open photo: ${label}`} sx={{ borderRadius: 1.5 }}>
        <ChatPhoto practiceId={practiceId} leadId={leadId} photoId={photo.photoId} alt={label} size={96} />
      </ButtonBase>
      <Typography variant="caption" sx={{ fontSize: '12px' }}>{label}</Typography>
    </Stack>
  );
}

ChatPhotoThumb.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  photo: PropTypes.shape({ photoId: PropTypes.string, angle: PropTypes.string, title: PropTypes.string }).isRequired,
  onOpen: PropTypes.func.isRequired,
};
ChatPhotoThumb.defaultProps = { practiceId: undefined, leadId: undefined };

/** Lightbox with prev/next across every photo in the transcript. */
export function ChatPhotoLightbox({ practiceId, leadId, photos, index, onIndexChange, onClose }) {
  const open = index !== null && index >= 0 && index < photos.length;
  const photo = open ? photos[index] : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {photo && (
        <Stack alignItems="center" spacing={1} sx={{ p: 2 }}>
          <Typography variant="subtitle2">{`${photoCaption(photo)} (${index + 1} of ${photos.length})`}</Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', justifyContent: 'center' }}>
            <IconButton aria-label="Previous photo" disabled={index === 0} onClick={() => onIndexChange(index - 1)}>
              <span aria-hidden>‹</span>
            </IconButton>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <ChatPhoto key={photo.photoId} practiceId={practiceId} leadId={leadId} photoId={photo.photoId} alt={photoCaption(photo)} size="min(70vh, 100%)" contain />
            </Box>
            <IconButton aria-label="Next photo" disabled={index === photos.length - 1} onClick={() => onIndexChange(index + 1)}>
              <span aria-hidden>›</span>
            </IconButton>
          </Stack>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <Typography variant="button">Close</Typography>
          </IconButton>
        </Stack>
      )}
    </Dialog>
  );
}

ChatPhotoLightbox.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  photos: PropTypes.arrayOf(PropTypes.shape({ photoId: PropTypes.string, angle: PropTypes.string, title: PropTypes.string })).isRequired,
  index: PropTypes.number,
  onIndexChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
ChatPhotoLightbox.defaultProps = { practiceId: undefined, leadId: undefined, index: null };
